import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowBack as ArrowBackIcon, Check as CheckIcon } from "@mui/icons-material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import Logger from "../../../services/Logger";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, IconButton, InputAdornment, Tooltip, TextField, Typography } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { SITE_BG, SURFACE } from "../../../dark-theme/palette";
import {
    getPdfDownloadUseDisplayClue,
    PDF_FORMAT_DEFAULT_KEY,
} from "../../../admin/pzl_common/PdfFormatDialog";
import { trackEvent } from "../../../services/GoogleAnalytics";
import {
    calculateProgress,
    findFilledCoordByWord,
    findWordInGrid,
    getFoundCellKeySetFromWords,
    getHintPatternForWordCells,
    hintPatternHasRevealedLetter,
    normalizeWord,
    removeHyphens
} from "../../puzzles/common/utils/PuzzlePlayLogic";
import CrossGrid from "../../puzzles/crossword/CrossGrid";
import CrossWordCluesPanel from "../../puzzles/crossword/components/CrossWordCluesPanel";
import CrossService from "../../puzzles/crossword/CrossService";
import {
    buildCrossMeaningsMapsFromWordsWithMeanings,
    lookupCrossWordMeaningPreview,
} from "../../puzzles/crossword/crossMeaningPreviewUtils";
import HintPatternBoxes from "../../puzzles/crossword/components/HintPatternBoxes";
import { getLocalizedStoryFromTemplate } from "../../../admin/pzl_common/getEnStoryFromTemplate";
import StoryPlayStoryAccordion from "../../story/StoryPlayStoryAccordion";
import StoryPlaySelectedWordPanel from "../../story/StoryPlaySelectedWordPanel";
import {
    buildCellToClueNumberMap,
    buildPuzzleWordMatchSet,
    buildStoryTokenToClueNumberMap,
    getFirstPositionForStoryClue,
    renderStoryWordFindMarkers,
} from "../../story/storyPlayUtils";
import CustomSnackbar from "../../components/CustomSnackbar";
import SocialShareDropdown from "../../puzzles/common/components/SocialShareDropdown";
import { usePagesTranslation } from "../../locales";

const StaticCrossPuzzleMain = ({
    puzzleData,
    dictionaryLanguage = "english",
    storyPlayLayout = false,
    isHowtoSampleDemo = false,
}) => {
    const { t } = usePagesTranslation();
    // State for found words (initially empty) - must be called before any early returns
    const [wordsFound, setWordsFound] = useState([]);
    const [wordsDrawerOpen, setWordsDrawerOpen] = useState(true);
    const [highlightCells, setHighlightCells] = useState([]);
    const [selectedWord, setSelectedWord] = useState(null);
    const [selectedWords, setSelectedWords] = useState([]);
    const [wordInputs, setWordInputs] = useState({});
    const [localWordInputValues, setLocalWordInputValues] = useState({});
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
    const [isBoldFont, setIsBoldFont] = useState(false);
    const [crossMeaningsLookup, setCrossMeaningsLookup] = useState({ wordMap: {}, clueMap: {} });
    const puzzleGridRef = useRef(null);

    useEffect(() => {
        trackEvent('entered', { name: 'StaticCrossPuzzleMain' });
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!storyPlayLayout) {
            setCrossMeaningsLookup({ wordMap: {}, clueMap: {} });
            return undefined;
        }
        const tplId = puzzleData?.template?.id;
        if (!tplId) {
            setCrossMeaningsLookup({ wordMap: {}, clueMap: {} });
            return undefined;
        }
        (async () => {
            try {
                const wordsWithMeanings = await CrossService.getWordsWithMeanings(String(tplId));
                if (cancelled) return;
                if (Array.isArray(wordsWithMeanings) && wordsWithMeanings.length > 0) {
                    setCrossMeaningsLookup(buildCrossMeaningsMapsFromWordsWithMeanings(wordsWithMeanings));
                } else {
                    setCrossMeaningsLookup({ wordMap: {}, clueMap: {} });
                }
            } catch (e) {
                Logger.warn("How-to story play: could not load word meanings", e);
                if (!cancelled) setCrossMeaningsLookup({ wordMap: {}, clueMap: {} });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [storyPlayLayout, puzzleData?.template?.id]);

    // Process puzzle data
    const processedData = useMemo(() => {
        if (!puzzleData) return null;

        // Parse filledCoordinates from template
        let filledCoordinates = [];
        if (puzzleData.template?.filledCoordinates) {
            try {
                filledCoordinates = JSON.parse(puzzleData.template.filledCoordinates);
            } catch (error) {
                Logger.error('Error parsing filledCoordinates:', error);
                filledCoordinates = [];
            }
        }

        // Create grid from letters string, or from filledCoordinates when letters is missing
        let grid = [];
        if (puzzleData.letters) {
            const lines = puzzleData.letters.split('\n');
            const paddedLines = lines.slice(0, 12);
            while (paddedLines.length < 12) {
                paddedLines.push('');
            }

            grid = paddedLines.map(line => {
                const chars = line.split('').map(char => char.toUpperCase());
                while (chars.length < 12) {
                    chars.push(' ');
                }
                return chars.slice(0, 12);
            });
        }
        // Fallback: build grid from filledCoordinates so letters show when words are found
        if (grid.length === 0 && filledCoordinates.length > 0) {
            grid = Array.from({ length: 12 }, () => Array(12).fill(' '));
            filledCoordinates.forEach(coord => {
                if (coord.positions) {
                    coord.positions.forEach(pos => {
                        const r = pos.row;
                        const c = pos.col;
                        if (r >= 0 && r < 12 && c >= 0 && c < 12 && pos.value) {
                            grid[r][c] = String(pos.value).toUpperCase();
                        }
                    });
                }
            });
        }

        // Parse words from template details
        let words = [];
        if (puzzleData.template?.details?.scores) {
            words = puzzleData.template.details.scores.map(score => score.word || score.item?.word || '').filter(w => w);
        }

        return {
            words,
            filledCoordinates,
            grid,
            title: puzzleData.title || 'Crossword Puzzle',
            type: 'CROSSWORD'
        };
    }, [puzzleData]);

    const progressPercentage = processedData?.words?.length > 0 
        ? Math.round((wordsFound.length / processedData.words.length) * 100) 
        : 0;

    const canDownloadPdf = useMemo(() => {
        const templateId = puzzleData?.templateId ?? puzzleData?.template?.id ?? puzzleData?.id;
        if (!templateId) return false;
        if (isHowtoSampleDemo) return true;
        const total = processedData?.words?.length ?? 0;
        if (!total) return false;
        return calculateProgress(wordsFound.length, total) === 100;
    }, [
        isHowtoSampleDemo,
        puzzleData?.id,
        puzzleData?.template?.id,
        puzzleData?.templateId,
        processedData?.words?.length,
        wordsFound.length,
    ]);

    // Get the first character position of a word
    const getFirstPosition = (coord) => {
        if (!coord.positions || coord.positions.length === 0) return null;
        if (coord.direction === "scattered") return null;
        let firstPos;
        if (coord.reversed) {
            firstPos = coord.positions[coord.positions.length - 1];
        } else {
            if (coord.direction === "vertical") {
                firstPos = coord.positions.reduce((min, pos) => pos.row < min.row ? pos : min);
            } else if (coord.direction === "horizontal") {
                firstPos = coord.positions.reduce((min, pos) => pos.col < min.col ? pos : min);
            } else {
                firstPos = coord.positions[0];
            }
        }
        return firstPos;
    };

    // Calculate word numbers: assign numbers to words based on their starting positions
    const wordNumbers = useMemo(() => {
        if (!processedData?.filledCoordinates || processedData.filledCoordinates.length === 0) {
            return new Map();
        }
        const startingPositions = new Map();
        processedData.filledCoordinates.forEach(coord => {
            if (coord.direction === "scattered") return;
            const firstPos = getFirstPosition(coord);
            if (!firstPos) return;
            const cellKey = `${firstPos.row},${firstPos.col}`;
            if (!startingPositions.has(cellKey)) {
                startingPositions.set(cellKey, { row: firstPos.row, col: firstPos.col });
            }
        });
        const sortedPositions = Array.from(startingPositions.values()).sort((a, b) => {
            if (a.row !== b.row) return a.row - b.row;
            return a.col - b.col;
        });
        const cellToNumber = new Map();
        sortedPositions.forEach((pos, index) => {
            cellToNumber.set(`${pos.row},${pos.col}`, index + 1);
        });
        return cellToNumber;
    }, [processedData?.filledCoordinates]);

    const storyPresentation = useMemo(() => {
        if (!storyPlayLayout || !puzzleData?.template || !processedData?.filledCoordinates?.length) {
            return {
                titleAboveGrid: "",
                bodyText: "",
                imageUrl: "",
                matchSet: new Set(),
                tokenToClueNumber: new Map(),
            };
        }
        const { title, text, imageUrl } = getLocalizedStoryFromTemplate(puzzleData.template);
        const coords = processedData.filledCoordinates;
        return {
            titleAboveGrid: title || "",
            bodyText: typeof text === "string" ? text : "",
            imageUrl: typeof imageUrl === "string" ? imageUrl : "",
            matchSet: buildPuzzleWordMatchSet(coords),
            tokenToClueNumber: buildStoryTokenToClueNumberMap(coords),
        };
    }, [storyPlayLayout, puzzleData?.template, processedData?.filledCoordinates]);

    const storySelectedClueNumber = useMemo(() => {
        if (!storyPlayLayout || !selectedWord || !processedData?.filledCoordinates?.length) {
            return null;
        }
        const coordResolved = findFilledCoordByWord(processedData.filledCoordinates, selectedWord);
        if (!coordResolved || coordResolved.direction === "scattered") return null;
        const first = getFirstPositionForStoryClue(coordResolved);
        if (!first) return null;
        const cellMap = buildCellToClueNumberMap(processedData.filledCoordinates);
        return cellMap.get(`${first.row},${first.col}`) ?? null;
    }, [storyPlayLayout, selectedWord, processedData?.filledCoordinates]);

    const storySelectedMeaningPreview = useMemo(() => {
        if (!storyPlayLayout || !selectedWord || !processedData?.filledCoordinates?.length) {
            return "";
        }
        const coord = findFilledCoordByWord(processedData.filledCoordinates, selectedWord);
        if (!coord?.text) return "";
        const direction =
            coord.direction ||
            (coord.positions?.length > 1 && coord.positions[0].row === coord.positions[1].row
                ? "horizontal"
                : "vertical");
        const firstPos = getFirstPosition(coord);
        const wordNumber = firstPos ? wordNumbers.get(`${firstPos.row},${firstPos.col}`) : null;
        const coordDisplay =
            coord.word != null && String(coord.word).trim() !== "" ? String(coord.word) : "";
        return (
            lookupCrossWordMeaningPreview(
                crossMeaningsLookup.wordMap,
                crossMeaningsLookup.clueMap,
                coord.text,
                coordDisplay,
                wordNumber,
                direction
            ) || ""
        );
    }, [
        storyPlayLayout,
        selectedWord,
        processedData?.filledCoordinates,
        crossMeaningsLookup,
        wordNumbers,
    ]);

    const hintRevealCells = useMemo(() => [], []);

    const foundCellKeySet = useMemo(
        () => getFoundCellKeySetFromWords(processedData?.filledCoordinates, wordsFound),
        [processedData?.filledCoordinates, wordsFound]
    );

    const getWordNumberForCoord = (coord) => {
        const firstPos = getFirstPosition(coord);
        if (!firstPos) return null;
        return wordNumbers.get(`${firstPos.row},${firstPos.col}`) || null;
    };

    // Find words from highlighted cells that have empty cells (for "Fill in the cells" section)
    const updateWordInputsForHighlightedCells = (highlightedCells) => {
        if (!processedData?.filledCoordinates || !highlightedCells || highlightedCells.length === 0) {
            setWordInputs({});
            return;
        }
        const newWordInputs = {};
        processedData.filledCoordinates.forEach(coord => {
            if (!coord.text || !coord.positions || coord.positions.length === 0) return;
            const normalizedWord = normalizeWord(coord.text);
            if (wordsFound.includes(normalizedWord)) return;
            const wordCells = coord.positions.map(pos => ({ row: pos.row, col: pos.col }));
            const allCellsHighlighted = wordCells.every(cell =>
                highlightedCells.some(hc => hc.row === cell.row && hc.col === cell.col)
            );
            if (!allCellsHighlighted) return;
            const direction = coord.direction ||
                (wordCells.length > 1 && wordCells[0].row === wordCells[1].row ? 'horizontal' : 'vertical');
            const wordKey = `${coord.text}-${direction}-${wordCells[0].row}-${wordCells[0].col}`;
            const wordNumber = getWordNumberForCoord(coord);
            newWordInputs[wordKey] = {
                word: coord.text,
                cells: wordCells,
                direction: direction,
                expectedWord: coord.text,
                inputValue: '',
                number: wordNumber
            };
        });
        setWordInputs(newWordInputs);
    };

    useEffect(() => {
        updateWordInputsForHighlightedCells(highlightCells);
    }, [highlightCells, wordsFound]);

    // Clear local values when a word is removed from wordInputs (e.g. after correct answer)
    useEffect(() => {
        setLocalWordInputValues(prev => {
            const next = { ...prev };
            let changed = false;
            Object.keys(prev).forEach(key => {
                if (!(key in wordInputs)) {
                    delete next[key];
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [wordInputs]);

    // Find all words that contain a specific cell
    const findWordsContainingCell = (row, col) => {
        if (!processedData?.filledCoordinates) return [];

        const wordsWithCells = [];
        processedData.filledCoordinates.forEach(coord => {
            if (!coord.positions || coord.positions.length === 0) return;

            // Check if this word contains the clicked cell
            const containsCell = coord.positions.some(pos => pos.row === row && pos.col === col);
            if (containsCell && coord.text) {
                const cells = coord.positions.map(pos => ({ row: pos.row, col: pos.col }));
                wordsWithCells.push({ word: coord.text, cells });
            }
        });

        return wordsWithCells;
    };

    // Handle cell click
    const handleClickedWords = (_word, cells) => {
        // For crossword puzzles, when a cell is clicked, highlight all words containing that cell
        if (!cells || cells.length === 0) {
            setHighlightCells([]);
            setSelectedWords([]);
            setSelectedWord(null);
            return;
        }

        // Get the last clicked cell (most recent)
        const lastCell = cells[cells.length - 1];
        if (!lastCell) {
            setHighlightCells([]);
            setSelectedWords([]);
            setSelectedWord(null);
            return;
        }

        // Find all words containing this cell
        const wordsContainingCell = findWordsContainingCell(lastCell.row, lastCell.col);

        // Collect all cells from all words containing the clicked cell
        const allHighlightCells = [];
        const selectedWordList = [];
        wordsContainingCell.forEach(({ word, cells: wordCells }) => {
            selectedWordList.push(word);
            wordCells.forEach(cell => {
                // Avoid duplicates
                if (!allHighlightCells.some(c => c.row === cell.row && c.col === cell.col)) {
                    allHighlightCells.push({ row: cell.row, col: cell.col });
                }
            });
        });

        setHighlightCells(allHighlightCells);
        setSelectedWords(selectedWordList); // Update selected words for highlighting in panel
        setSelectedWord(null); // Clear single word selection when clicking a new cell
    };

    // Handle word selection from panel
    const handleWordSelect = (word) => {
        setSelectedWord(word);
        // Highlight the word on the grid
        if (word && processedData?.filledCoordinates) {
            const cells = findWordInGrid(word, processedData.filledCoordinates);
            if (cells && cells.length > 0) {
                // Convert cells to highlightCells format without charIndex (same color for all)
                const highlightCellsSameColor = cells.map((cell) => ({
                    row: cell.row,
                    col: cell.col
                }));
                setHighlightCells(highlightCellsSameColor);
                // Update selected words
                setSelectedWords([word]);
            } else {
                setHighlightCells([]);
                setSelectedWords([]);
            }
        } else {
            setHighlightCells([]);
            setSelectedWords([]);
        }
    };

    // Clear selection
    const handleClearClickedWords = () => {
        puzzleGridRef.current?.clearSelection();
        setHighlightCells([]);
        setSelectedWord(null);
        setSelectedWords([]);
        setWordInputs({});
    };

    const handleWordInputChange = (wordKey, value) => {
        setLocalWordInputValues(prev => ({ ...prev, [wordKey]: value }));
    };

    const handleWordInputSubmit = (wordKey) => {
        const wordInput = wordInputs[wordKey];
        if (!wordInput) return;
        const inputValue = localWordInputValues[wordKey] ?? wordInput.inputValue ?? '';
        const normalizedInput = normalizeWord(inputValue);
        const normalizedExpected = normalizeWord(wordInput.expectedWord);
        const inputWithoutHyphens = removeHyphens(normalizedInput);
        const expectedWithoutHyphens = removeHyphens(normalizedExpected);
        if (normalizedInput === normalizedExpected || inputWithoutHyphens === expectedWithoutHyphens) {
            const wordToAdd = normalizedExpected;
            if (!wordsFound.includes(wordToAdd)) {
                setWordsFound(prev => [...prev, wordToAdd]);
                puzzleGridRef.current?.markClickedCellsAsFound(wordInput.cells);
                const newWordInputs = { ...wordInputs };
                delete newWordInputs[wordKey];
                setWordInputs(newWordInputs);
                setLocalWordInputValues(prev => {
                    const next = { ...prev };
                    delete next[wordKey];
                    return next;
                });
                setHighlightCells([]);
            }
        }
    };

    const handleWordEnterSubmitForStory = useCallback(
        (expectedWord, enteredValue) => {
            if (!processedData?.filledCoordinates || !expectedWord) return true;
            const normalizedInput = normalizeWord(enteredValue || "");
            const normalizedExpected = normalizeWord(expectedWord);
            const inputWithoutHyphens = removeHyphens(normalizedInput);
            const expectedWithoutHyphens = removeHyphens(normalizedExpected);
            if (normalizedInput === normalizedExpected || inputWithoutHyphens === expectedWithoutHyphens) {
                const wordToAdd = normalizedExpected;
                if (!wordsFound.includes(wordToAdd)) {
                    setWordsFound((prev) => [...prev, wordToAdd]);
                    const cells = findWordInGrid(wordToAdd, processedData.filledCoordinates);
                    if (cells?.length > 0 && puzzleGridRef.current) {
                        puzzleGridRef.current.markClickedCellsAsFound(cells);
                    }
                    setHighlightCells([]);
                    setWordInputs({});
                }
                return true;
            }
            return false;
        },
        [processedData?.filledCoordinates, wordsFound]
    );

    const handlePdf = useCallback(async () => {
        const total = processedData?.words?.length ?? 0;
        if (
            !isHowtoSampleDemo &&
            (!total || calculateProgress(wordsFound.length, total) !== 100)
        ) {
            return;
        }
        const templateId = puzzleData?.templateId ?? puzzleData?.template?.id ?? puzzleData?.id;
        if (!templateId) {
            Logger.error("StaticCrossPuzzleMain: No template ID for PDF download");
            return;
        }
        const savedFormat =
            (typeof window !== "undefined" ? window.localStorage?.getItem(PDF_FORMAT_DEFAULT_KEY) : null) || "letter";
        const format = savedFormat === "a4" ? "a4" : "letter";
        const pdfUrl = format === "a4" ? puzzleData?.pdfA4Url : puzzleData?.pdfLetterUrl;
        setIsPdfDownloading(true);
        document.body.style.cursor = "wait";
        try {
            await CrossService.recordAndDownloadPdf(templateId, format, {
                pdfUrl: pdfUrl || undefined,
                title: processedData?.title || puzzleData?.title || "puzzle",
                useDisplayClue: getPdfDownloadUseDisplayClue(),
            });
        } catch (error) {
            Logger.error("PDF download error:", error);
        } finally {
            setIsPdfDownloading(false);
            document.body.style.cursor = "default";
        }
    }, [isHowtoSampleDemo, puzzleData, processedData, wordsFound.length]);

    const handleShareCopyLink = useCallback(() => {
        setShareSnackbarOpen(true);
    }, []);

    if (!processedData) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>No puzzle data available</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: { xs: 'auto', md: 'min(720px, 90vh)' },
            backgroundColor: SITE_BG,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box'
        }}>
            {/* Main Content: column on mobile; grid + clues row on md+ */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'stretch' },
                gap: { xs: 2, md: 2 },
                overflowX: 'hidden',
                p: 2,
                backgroundColor: SURFACE.sunken,
                minHeight: { xs: 'auto', md: 'min(680px, 88vh)' },
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
            }}>
                {/* Puzzle Grid Area (left column on md+) */}
                <Box sx={{
                    flex: { xs: '1 1 auto', md: '1 1 0' },
                    display: 'flex',
                    flexDirection: 'column',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    position: 'relative',
                    minWidth: 0,
                    minHeight: 0,
                    backgroundColor: SURFACE.card,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    width: '100%',
                    boxSizing: 'border-box',
                    alignSelf: 'stretch'
                }}>
                    {/* Title */}
                    <Box sx={{
                        p: 2,
                        pb: 1,
                        pt: 2,
                        borderBottom: `1px solid ${SURFACE.border}`,
                        flexShrink: 0
                    }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: '#3BA666',
                                textAlign: 'center'
                            }}
                        >
                            {storyPlayLayout && String(storyPresentation.titleAboveGrid || "").trim()
                                ? storyPresentation.titleAboveGrid
                                : processedData.title}
                        </Typography>
                    </Box>
                    {/* Top Action Bar */}
                    <Box sx={{
                        p: 2,
                        pb: 1,
                        flexShrink: 0,
                        overflowX: 'hidden',
                        overflowY: 'hidden',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%'
                        }}>
                            {/* Back Button */}
                            <Tooltip title="Go back" arrow>
                                <IconButton
                                    sx={{
                                        backgroundColor: SURFACE.panel,
                                        borderRadius: '50%',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        flexShrink: 0
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip
                                title={
                                    isBoldFont
                                        ? t("puzzle.playGrid.switch-light")
                                        : t("puzzle.playGrid.switch-bold")
                                }
                                arrow
                            >
                                <IconButton
                                    onClick={() => setIsBoldFont(!isBoldFont)}
                                    sx={{
                                        backgroundColor: isBoldFont
                                            ? "#3BA666"
                                            : SURFACE.panel,
                                        borderRadius: "50%",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                        color: isBoldFont ? "#fff" : "inherit",
                                        flexShrink: 0,
                                        "&:hover": {
                                            backgroundColor: isBoldFont
                                                ? "#2E8B57"
                                                : SURFACE.hover,
                                        },
                                    }}
                                >
                                    <FormatBoldIcon />
                                </IconButton>
                            </Tooltip>

                            {/* PDF — how-to sample: enabled anytime; live play requires every word found */}
                            <Tooltip
                                title={
                                    isHowtoSampleDemo || canDownloadPdf
                                        ? t("puzzle.playCross.download-pdf")
                                        : t("puzzle.playGrid.download-pdf-locked-complete", {
                                              current: wordsFound.length,
                                              total: processedData.words.length,
                                          })
                                }
                                arrow
                            >
                                <Box component="span" sx={{ display: "inline-flex" }}>
                                    <IconButton
                                        onClick={handlePdf}
                                        disabled={isPdfDownloading || !canDownloadPdf}
                                        sx={{
                                            backgroundColor: SURFACE.panel,
                                            borderRadius: "50%",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                            flexShrink: 0,
                                            cursor: isPdfDownloading ? "wait" : "pointer",
                                            color: isHowtoSampleDemo || canDownloadPdf ? "#3BA666" : "inherit",
                                            "&:hover": {
                                                backgroundColor: SURFACE.hover,
                                            },
                                            "&.Mui-disabled": {
                                                opacity: 0.7,
                                            },
                                        }}
                                    >
                                        <PictureAsPdfIcon />
                                    </IconButton>
                                </Box>
                            </Tooltip>

                            {/* Share — same dropdown as live crossword play */}
                            <Box sx={{ flexShrink: 0 }}>
                                <SocialShareDropdown
                                    puzzleTitle={processedData.title || puzzleData?.title || "Puzzle"}
                                    puzzleId={
                                        puzzleData?.templateId ??
                                        puzzleData?.template?.id ??
                                        puzzleData?.id ??
                                        ""
                                    }
                                    puzzleType="CROSSWORD"
                                    onCopyLink={handleShareCopyLink}
                                    isDemo={false}
                                />
                            </Box>

                            {/* Clear Selection Button */}
                            <Tooltip title="Clear selected words" arrow>
                                <IconButton
                                    onClick={handleClearClickedWords}
                                    sx={{
                                        backgroundColor: SURFACE.panel,
                                        borderRadius: '50%',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        flexShrink: 0
                                    }}
                                >
                                    <RestartAltIcon />
                                </IconButton>
                            </Tooltip>

                            {/* Fullscreen Toggle Button */}
                            <Tooltip title="Show puzzle fullscreen" arrow>
                                <IconButton
                                    sx={{
                                        backgroundColor: SURFACE.panel,
                                        borderRadius: '50%',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        flexShrink: 0
                                    }}
                                >
                                    <FullscreenIcon />
                                </IconButton>
                            </Tooltip>

                            {/* Words List Toggle Button */}
                            <Tooltip title="Toggle Words List" arrow>
                                <IconButton
                                    sx={{
                                        backgroundColor: '#3BA666',
                                        borderRadius: '50%',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        color: '#fff',
                                        flexShrink: 0
                                    }}
                                >
                                    <LibraryBooksIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        {isHowtoSampleDemo && (
                            <Box sx={{ mt: 1, textAlign: "center", px: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "text.secondary",
                                        fontStyle: "italic",
                                        fontSize: "0.8rem",
                                        lineHeight: 1.45,
                                        display: "block",
                                    }}
                                >
                                    {t("howto.crossword.sample-demo-note")}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Puzzle Grid - content flows, grid does not shrink when Fill section appears */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        overflowX: 'hidden',
                        overflowY: 'visible',
                        pt: 2,
                        pl: 0,
                        pr: 0,
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        flexShrink: 0,
                        pb: 0.5
                    }}>
                        <Box sx={{
                            width: '100%',
                            maxWidth: '100%',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            minWidth: 0,
                            aspectRatio: '1',
                            flexShrink: 0,
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                            p: 2
                        }}>
                            <CrossGrid
                                ref={puzzleGridRef}
                                grid={processedData.grid}
                                words={processedData.words}
                                wordsFound={wordsFound}
                                onClearClickedWords={handleClearClickedWords}
                                onClickedWords={handleClickedWords}
                                gridSize={12}
                                isBoldFont={isBoldFont}
                                filledCoordinates={processedData.filledCoordinates}
                                hintRevealCells={hintRevealCells}
                                highlightCells={highlightCells}
                                hideAllWords={true}
                                clueNumberFontPx={11}
                            />
                        </Box>

                        {/* Progress Bar */}
                        <Box sx={{ mt: 1, width: '100%', px: 2, pb: 0 }}>
                            <Box sx={{ p: 1.5, pt: 1.5, pb: 1, backgroundColor: SURFACE.sunken, borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography
                                        variant="subtitle1"
                                        color="primary"
                                        sx={{
                                            fontWeight: 600,
                                            letterSpacing: '0.5px',
                                            fontSize: '0.95rem'
                                        }}
                                    >
                                        Crossword
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                    >
                                        <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                            ({processedData.words.length} words)
                                        </Box>
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {t("puzzle.playCross.find-all-words")}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progressPercentage}
                                            sx={{ height: 4, borderRadius: 2, flex: 1 }}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content', fontWeight: 500 }}>
                                            {wordsFound.length} / {processedData.words.length}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* Word Input Boxes for Highlighted Empty Words */}
                        {Object.keys(wordInputs).length > 0 ? (
                            <Box sx={{
                                mt: 2,
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                                boxSizing: 'border-box',
                                p: 1.5,
                                backgroundColor: SURFACE.sunken,
                                borderRadius: 1,
                                border: `1px solid ${SURFACE.border}`,
                                mx: 2,
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' },
                                gap: 2,
                                flexWrap: 'wrap'
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#3BA666', flexShrink: 0 }}>
                                    {t("puzzle.playCross.fill-cells")}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                                    {Object.entries(wordInputs).map(([wordKey, wordInput]) => (
                                        <Box
                                            key={wordKey}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                gap: { xs: 1.5, sm: 1 },
                                                p: 1,
                                                backgroundColor: SURFACE.card,
                                                borderRadius: 1,
                                                border: `1px solid ${SURFACE.border}`,
                                                width: '100%',
                                                maxWidth: '100%',
                                                minWidth: 0,
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                                {wordInput.number && (
                                                    <Box
                                                        sx={{
                                                            width: '21px',
                                                            height: '21px',
                                                            minWidth: '21px',
                                                            minHeight: '21px',
                                                            flexShrink: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: '#C4FFE0',
                                                            borderRadius: '50%',
                                                            border: '1px solid #2E8B57',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                            color: '#000',
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                        }}
                                                    >
                                                        {wordInput.number}
                                                    </Box>
                                                )}
                                                <Typography variant="body2" component="span" sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                    {wordInput.direction === 'horizontal' ? t("puzzle.playCross.across") : t("puzzle.playCross.down")}:
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder={t("puzzle.playCross.enter-word", { count: removeHyphens(wordInput.expectedWord).length })}
                                                    value={localWordInputValues[wordKey] ?? wordInput.inputValue ?? ''}
                                                    onChange={(e) => handleWordInputChange(wordKey, e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleWordInputSubmit(wordKey); }}
                                                    inputProps={{
                                                        maxLength: removeHyphens(wordInput.expectedWord).length,
                                                        style: { textTransform: 'uppercase', textAlign: 'center', fontSize: '1.1rem', fontWeight: 600 }
                                                    }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end" sx={{ mr: 0.5 }}>
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: 22,
                                                                        height: 22,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: '#e8f5e9',
                                                                        border: '1px solid #3BA666',
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 600,
                                                                        color: '#2E8B57'
                                                                    }}
                                                                >
                                                                    {(localWordInputValues[wordKey] ?? wordInput.inputValue ?? '').length}
                                                                </Box>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: SURFACE.card,
                                                            '&:hover': { backgroundColor: SURFACE.hover }
                                                        },
                                                        '& .MuiInputBase-input::placeholder': {
                                                            fontSize: '0.875rem',
                                                            fontWeight: 400,
                                                            opacity: 0.6,
                                                            textTransform: 'none'
                                                        }
                                                    }}
                                                />
                                                <IconButton
                                                    onClick={() => handleWordInputSubmit(wordKey)}
                                                    disabled={(() => {
                                                        const inputVal = (localWordInputValues[wordKey] ?? wordInput.inputValue ?? '').trim();
                                                        if (!inputVal) return true;
                                                        const inputNorm = normalizeWord(inputVal);
                                                        const expectedNorm = normalizeWord(wordInput.expectedWord);
                                                        const inputClean = removeHyphens(inputNorm);
                                                        const expectedClean = removeHyphens(expectedNorm);
                                                        if (inputClean.length !== expectedClean.length) return true;
                                                        return inputNorm !== expectedNorm && inputClean !== expectedClean;
                                                    })()}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#3BA666',
                                                        color: '#fff',
                                                        '&:hover': { backgroundColor: '#2E8B57' },
                                                        '&.Mui-disabled': { backgroundColor: SURFACE.border, color: 'text.disabled' }
                                                    }}
                                                >
                                                    <CheckIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            {(() => {
                                                const rowPattern = getHintPatternForWordCells(
                                                    wordInput.expectedWord,
                                                    wordInput.cells,
                                                    hintRevealCells,
                                                    foundCellKeySet
                                                );
                                                return rowPattern && hintPatternHasRevealedLetter(rowPattern) ? (
                                                    <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' }, width: '100%', mt: 0.25 }}>
                                                        <HintPatternBoxes pattern={rowPattern} boxSize={22} />
                                                    </Box>
                                                ) : null;
                                            })()}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ) : null}
                    </Box>
                </Box>

                {/* Words / clues panel — below grid on xs; right column on md+ */}
                <Box sx={{
                    flex: { xs: '1 1 auto', md: '1 1 0' },
                    minWidth: 0,
                    minHeight: { xs: 280, md: 0 },
                    backgroundColor: SURFACE.sunken,
                    borderRadius: 2,
                    mt: { xs: 0, md: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    alignSelf: 'stretch'
                }}>
                    <Box sx={{
                        flex: 1,
                        minHeight: { xs: 260, md: 0 },
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        WebkitOverflowScrolling: 'touch',
                        gap: storyPlayLayout ? 2 : 0,
                    }}>
                    {storyPlayLayout &&
                    puzzleData?.template &&
                    (storyPresentation.titleAboveGrid ||
                        storyPresentation.imageUrl ||
                        (storyPresentation.bodyText && storyPresentation.bodyText.trim())) ? (
                        <>
                            <StoryPlayStoryAccordion
                                summaryTitle={
                                    String(storyPresentation.titleAboveGrid || "").trim()
                                        ? String(storyPresentation.titleAboveGrid).trim()
                                        : t("puzzle.playCross.story-card-heading")
                                }
                                imageUrl={storyPresentation.imageUrl || undefined}
                            >
                                {storyPresentation.bodyText?.trim()
                                    ? storyPresentation.matchSet.size > 0
                                        ? renderStoryWordFindMarkers(
                                              storyPresentation.bodyText,
                                              storyPresentation.matchSet,
                                              storyPresentation.tokenToClueNumber,
                                              processedData.filledCoordinates,
                                              handleWordSelect,
                                              "numberedCircle",
                                              null,
                                              null,
                                              storySelectedClueNumber,
                                              wordsFound
                                          )
                                        : storyPresentation.bodyText
                                    : null}
                            </StoryPlayStoryAccordion>
                            <Box
                                sx={{
                                    flex: 1,
                                    minHeight: { xs: 240, md: 0 },
                                    backgroundColor: SURFACE.sunken,
                                    borderRadius: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                }}
                            >
                                <StoryPlaySelectedWordPanel
                                    filledCoordinates={processedData.filledCoordinates}
                                    wordsFound={wordsFound}
                                    selectedWord={selectedWord}
                                    hideAllWords={true}
                                    meaningPreview={storySelectedMeaningPreview}
                                    hintRevealCells={hintRevealCells}
                                    language={dictionaryLanguage}
                                    userPayPlan={null}
                                    onWordEnterSubmit={handleWordEnterSubmitForStory}
                                    onWordSelect={handleWordSelect}
                                />
                            </Box>
                        </>
                    ) : (
                    <CrossWordCluesPanel
                        filledCoordinates={processedData.filledCoordinates}
                        wordsFound={wordsFound}
                        onHide={() => {}}
                        selectedWord={selectedWord}
                        selectedWords={selectedWords}
                        onWordSelect={handleWordSelect}
                        hideAllWords={true}
                        hideVisibilityToggle={true}
                        language={dictionaryLanguage}
                        useCacheEndpoint={true}
                        hintRevealCells={hintRevealCells}
                        displayClue={
                            puzzleData?.displayClue ??
                            puzzleData?.template?.displayClue ??
                            null
                        }
                    />
                    )}
                    </Box>
                </Box>
            </Box>

            <CustomSnackbar
                open={shareSnackbarOpen}
                setOpen={setShareSnackbarOpen}
                message={t("puzzle.playCross.link-copied")}
                severity="success"
            />
        </Box>
    );
};

export default StaticCrossPuzzleMain;

